import { DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import Moment from "moment";
import { useEffect, useState } from "react";
import { MdOutlineVisibilityOff, MdVisibility } from "react-icons/md";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import SelectComponent from "../../components/Common/Select";
import {
  AgentName,
  BookingActions,
  fetchWebBookingStatus,
  getAgentStatus,
  getWebBookingActionStatus,
  webBookingStatus,
} from "../../components/Constants";
import ListPageLayout from "../../components/Layout";
import AssignedTo from "../../components/Modal/AssignedTo";
import Deletepopup from "../../components/Modal/Deletepopup";
import LeadStatus from "../../components/Modal/LeadStatus";
import SucessModal from "../../components/Modal/SucessModal";
import {
  formatDateTime,
  modifyBookingStatus,
  updatedAgentStatus,
  updatedBookingAction,
} from "../../components/Utils";
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";
import "./webBooking.scss";

const sensitiveReasons = [
  "Sexologists",
  "Andrologists",
  "Ayurvedic Sexologists",
];

const AboutSection = ({ specialization, about }) => {
  const [visibleAbout, setVisibleAbout] = useState(false);
  const isSensitive = sensitiveReasons.includes(specialization);

  if (!isSensitive) return <>{about || "-"}</>;

  return visibleAbout ? (
    <>
      <MdVisibility
        style={{ cursor: "pointer", marginRight: "5px" }}
        onClick={() => setVisibleAbout(false)}
      />
      {about || "-"}
    </>
  ) : (
    <MdOutlineVisibilityOff
      style={{ cursor: "pointer" }}
      onClick={() => setVisibleAbout(true)}
    />
  );
};

const WebBookingOnlineConsultation = () => {
  const [sort, setSort] = useState("newest");
  const [sortName, setSortName] = useState("Newest First");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [assignModal, setAssignModal] = useState({ id: "", status: false });
  const [leadStatusModal, setLeadStatusModal] = useState({
    id: "",
    status: false,
  });
  const [success, setSuccess] = useState(false);
  const [leadStatussuccess, setLeadStatusSuccess] = useState(false);
  const [loader, setLoader] = useState(true);

  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}booking-details/list`,
      initialParams: { sort, searchText: search },
    });

  const handleSortClick = useSortHandler(setSort, setSortName);
  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "booking-details",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const updateStatus = (url, id, value, updateFn) => {
    setLoader(true)
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}${url}/update/${id}`,
        updateFn(value),
      )
      .then(fetchData);
  };

  const successModalClose = () => {
    setSuccess(false);
    setLeadStatusSuccess(false);
    fetchData();
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Patient Name",
      dataIndex: "name",
      fixed: "left",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => Moment(text).format("DD-MM-YYYY LT"),
      width: 220,
    },
    {
      title: "Last updated on",
      dataIndex: "updatedAt",
      render: (text) => Moment(text).format("DD-MM-YYYY"),
      width: 220,
    },
    {
      title: "Doctor Name",
      dataIndex: "doctor_name",
      fixed: "left",
    },
    {
      title: "Doctor Specializations",
      dataIndex: "doctor_primary_specialization",
      render: (val) => val || "N/A",
    },
    {
      title: "Doctor Mobile No",
      dataIndex: "doctor_mobile",
      render: (val) => val || "N/A",
    },
    {
      title: "Patient Mobile No",
      dataIndex: "mobile",
    },
    {
      title: "Patient Email id",
      dataIndex: "email",
    },
    {
      title: "Age",
      dataIndex: "age",
    },
    {
      title: "Gender",
      dataIndex: "gender",
    },
    {
      title: "Relation",
      dataIndex: "relation",
    },
    {
      title: "Reason for Consultation",
      render: (_, record) => (
        <div style={{ minWidth: 250, wordBreak: "break-word" }}>
          <AboutSection
            about={record.about}
            specialization={record.doctor_primary_specialization}
          />
        </div>
      ),
    },
    {
      title: "Interest Received",
      dataIndex: "createdAt",
      render: (val) => Moment(val).format("MMMM DD, YYYY LT"),
    },
    {
      title: "Booking Requested Date & Time",
      render: (_, record) => formatDateTime(record.date, record.time),
    },
    {
      title: "Action",
      render: (_, record) => (
        <SelectComponent
          props={{
            Array: webBookingStatus,
            value: fetchWebBookingStatus(record),
            style: { color: "#CB1B5B", width: "380px" },
            onChange: (value) =>
              updateStatus(
                "booking-details",
                record._id,
                value,
                modifyBookingStatus,
              ),
          }}
        />
      ),
    },
    {
      title: "Booking Action",
      render: (_, record) => (
        <SelectComponent
          props={{
            Array: BookingActions,
            value: getWebBookingActionStatus(record),
            style: { color: "#CB1B5B", width: "370px" },
            onChange: (value) => {
              if (value === "Delete") {
                setDeleteId(record._id);
                setShowDelete(true);
              } else {
                updateStatus(
                  "booking-details",
                  record._id,
                  value,
                  updatedBookingAction,
                );
              }
            },
          }}
        />
      ),
    },
    {
      title: "Agent Name",
      render: (_, record) => (
        <SelectComponent
          props={{
            Array: AgentName,
            value: getAgentStatus(record),
            style: { color: "#CB1B5B", width: "140px" },
            onChange: (value) =>
              updateStatus(
                "booking-details",
                record._id,
                value,
                updatedAgentStatus,
              ),
          }}
        />
      ),
    },
    {
      title: "Delete Action",
      render: (_, record) => (
        <span
          className="text-primarycolor mx-2 cursor-pointer"
          onClick={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
        >
          <DeleteOutlined /> Delete
        </span>
      ),
    },
  ];

  useEffect(() => {
    records.length && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={<></>}
      addButton={<></>}
      titles={<></>}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination({
              ...pagination,
              limit: val,
              skip: 0,
              currentPage: 1,
            })
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource: records.map((el) => ({ ...el, key: el._id })),
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
      {assignModal.status && (
        <AssignedTo
          id={assignModal.id}
          setAssignModal={setAssignModal}
          setSuccess={setSuccess}
        />
      )}
      {success && (
        <SucessModal name="BD" post="Assigned" onClick={successModalClose} />
      )}
      {leadStatusModal.status && (
        <LeadStatus
          id={leadStatusModal.id}
          setLeadStatusModal={setLeadStatusModal}
          setLeadStatusSuccess={setLeadStatusSuccess}
        />
      )}
      {leadStatussuccess && (
        <SucessModal
          name="Lead Status"
          post="Updated"
          onClick={successModalClose}
        />
      )}
    </ListPageLayout>
  );
};

export default WebBookingOnlineConsultation;
